/**
 * ContractDashboard — In-game contract validation dashboard
 *
 * Runtime equivalent of scripts/dashboard.ts. Checks that all cross-system
 * contracts are intact without leaving the game. Runs in <100ms.
 *
 * DJ's spec: "I want this to show all of our API and handshaking between any
 * files still lights up green as we add features."
 *
 * Implementation split across:
 *   - `./types.ts`           — Category, CLOSE_CODE_NAMES
 *   - `./checks/content.ts`  — TOML / hotbar / equipment / loot / feedback / mob rendering
 *   - `./checks/runtime.ts`  — service registry / event bus / constants / data integrity / sync / multiplayer / server
 *   - `./checks/index.ts`    — runAllChecks aggregator
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { Check, Status } from '../DashboardShared';
import { statusColors, statusIcons } from '../DashboardShared';
import type { Category } from './types';
import { runAllChecks } from './checks';

export const ContractDashboardContent: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [apiHealth, setApiHealth] = useState<Category | null>(null);

  // Fetch /api/content/health and build a category from it
  useEffect(() => {
    let cancelled = false;
    const baseUrl = (window as any).__API_URL__ || `${window.location.protocol}//${window.location.hostname}:2567`;
    fetch(`${baseUrl}/api/content/health`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const checks: Check[] = [];
        const c = data.content || {};
        for (const [key, val] of Object.entries(c) as [string, any][]) {
          const count = val?.count ?? val;
          checks.push({
            id: `api_${key}`,
            label: key,
            status: typeof count === 'number' && count > 0 ? 'pass' : 'warn',
            detail: val?.coverage ? `${count} (${val.coverage} VFX)` : `${count}`,
          });
        }
        setApiHealth({ id: 'api-health', label: 'Content Health API', icon: '\uD83C\uDF10', checks });
      })
      .catch(() => {
        if (cancelled) return;
        setApiHealth({
          id: 'api-health', label: 'Content Health API', icon: '\uD83C\uDF10',
          checks: [{ id: 'api_unreachable', label: 'API status', status: 'warn', detail: 'Server unreachable' }],
        });
      });

    return () => { cancelled = true; };
  }, [refreshKey]);

  const { categories: localCategories, elapsed } = useMemo(() => runAllChecks(), [refreshKey]);
  const categories = [...localCategories, ...(apiHealth ? [apiHealth] : [])];

  const totals = useMemo(() => {
    let pass = 0, fail = 0, warn = 0;
    for (const cat of categories) {
      for (const c of cat.checks) {
        if (c.status === 'pass') pass++;
        else if (c.status === 'fail') fail++;
        else warn++;
      }
    }
    return { total: pass + fail + warn, pass, fail, warn };
  }, [categories]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const copyToClipboard = useCallback(() => {
    const lines: string[] = [];
    lines.push(`Contract Dashboard — ${totals.pass}/${totals.total} pass, ${totals.fail} fail, ${totals.warn} warn (${elapsed.toFixed(1)}ms)`);
    lines.push('');
    for (const cat of categories) {
      const catFail = cat.checks.filter(c => c.status === 'fail').length;
      const catPass = cat.checks.filter(c => c.status === 'pass').length;
      lines.push(`${cat.icon} ${cat.label} (${catPass}/${cat.checks.length})${catFail > 0 ? ' FAILING' : ''}`);
      for (const c of cat.checks) {
        const icon = c.status === 'pass' ? '[OK]' : c.status === 'fail' ? '[FAIL]' : '[WARN]';
        lines.push(`  ${icon} ${c.label} — ${c.detail}`);
      }
      lines.push('');
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [categories, totals, elapsed]);

  const overallStatus: Status = totals.fail > 0 ? 'fail' : totals.warn > 0 ? 'warn' : 'pass';
  const overallColor = statusColors[overallStatus];

  return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 6, fontSize: 12 }}>

        {/* ── Summary Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '6px 8px', borderRadius: 4,
          background: `${overallColor}15`, border: `1px solid ${overallColor}40`,
        }}>
          <div>
            <span style={{ fontWeight: 'bold', color: overallColor, fontSize: 14 }}>
              {totals.fail === 0 ? 'ALL CHECKS PASSED' : `${totals.fail} FAILED`}
            </span>
            <span style={{ color: '#888', marginLeft: 10, fontSize: 11 }}>
              {totals.pass}/{totals.total} pass
              {totals.warn > 0 && <> &middot; {totals.warn} warn</>}
              {totals.fail > 0 && <> &middot; <span style={{ color: '#e74c3c' }}>{totals.fail} fail</span></>}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#666', fontSize: 10 }}>{elapsed.toFixed(1)}ms</span>
            <button
              onClick={copyToClipboard}
              style={{
                padding: '3px 8px', fontSize: 11, borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)',
                background: copied ? 'rgba(46,204,113,0.2)' : 'rgba(255,255,255,0.08)',
                color: copied ? '#2ecc71' : '#ccc', cursor: 'pointer',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={refresh}
              style={{
                padding: '3px 8px', fontSize: 11, borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${(totals.pass / totals.total) * 100}%`,
            background: overallColor,
            transition: 'width 0.3s',
          }} />
        </div>

        {/* ── Check list ── */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {categories.map(cat => {
            const catPass = cat.checks.filter(c => c.status === 'pass').length;
            const catFail = cat.checks.filter(c => c.status === 'fail').length;
            const catColor = catFail > 0 ? '#e74c3c' : '#2ecc71';

            return (
              <CategorySection key={cat.id} category={cat} catPass={catPass} catColor={catColor} />
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6,
          fontSize: 10, color: '#666', display: 'flex', justifyContent: 'space-between',
        }}>
          <span>Runtime contract validator &middot; same checks as <code style={{ color: '#888' }}>make dashboard</code></span>
          <span>{totals.total} checks</span>
        </div>
      </div>
  );
};

// ── Collapsible Category ────────────────────────────────

const CategorySection: React.FC<{
  category: Category;
  catPass: number;
  catColor: string;
}> = ({ category, catPass, catColor }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: 4,
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      {/* Category header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '5px 8px', cursor: 'pointer',
          borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <span style={{ fontWeight: 'bold', color: '#e0e0ff', fontSize: 12 }}>
          {category.icon} {category.label}
          <span style={{ fontWeight: 'normal', color: catColor, marginLeft: 8, fontSize: 11 }}>
            {catPass}/{category.checks.length}
          </span>
        </span>
        <span style={{ color: '#666', fontSize: 10 }}>{collapsed ? '\u25B6' : '\u25BC'}</span>
      </div>

      {/* Check rows */}
      {!collapsed && (
        <div style={{ padding: '2px 0' }}>
          {category.checks.map(check => (
            <div
              key={check.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '2px 8px 2px 16px',
                background: check.status === 'fail' ? 'rgba(231,76,60,0.08)' : 'transparent',
              }}
            >
              <span style={{
                color: statusColors[check.status],
                fontWeight: 'bold',
                fontSize: 11,
                width: 12,
                textAlign: 'center',
                flexShrink: 0,
              }}>
                {statusIcons[check.status]}
              </span>
              <span style={{
                color: check.status === 'pass' ? '#aaa' : check.status === 'fail' ? '#e74c3c' : '#f39c12',
                fontSize: 11,
                minWidth: 140,
                flexShrink: 0,
              }}>
                {check.label}
              </span>
              <span style={{ color: '#666', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {check.detail}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
